import React, { useState } from 'react';
import PlotlyChart from '../../../components/charts/PlotlyChart';

const TOGGLE_GROUPS = [
  { key: 'sma_20', label: 'SMA 20', color: '#60a5fa' },
  { key: 'sma_50', label: 'SMA 50', color: '#3b82f6' },
  { key: 'sma_200', label: 'SMA 200', color: '#1d4ed8' },
  { key: 'ema_12', label: 'EMA 12', color: '#fbbf24' },
  { key: 'ema_26', label: 'EMA 26', color: '#f59e0b' },
  { key: 'bollinger', label: 'Bollinger', color: '#a78bfa' },
  { key: 'fibonacci', label: 'Fibonacci', color: '#c9a96e' },
  { key: 'pivots', label: 'Pivots', color: '#94a3b8' },
];

const DEFAULT_VISIBLE = ['sma_50', 'sma_200', 'bollinger'];

const PriceChartWithOverlays = ({ history }) => {
  const [visible, setVisible] = useState(new Set(DEFAULT_VISIBLE));

  if (!history?.series?.date?.length) {
    return <div className="text-xs text-gray-400 text-center py-12">No data.</div>;
  }

  const s = history.series;
  const dates = s.date;
  const ind = history.indicators_latest || {};

  const traces = [];

  // Candlesticks
  traces.push({
    type: 'candlestick',
    x: dates,
    open: s.open, high: s.high, low: s.low, close: s.close,
    name: 'Price',
    increasing: { line: { color: '#16a34a' } },
    decreasing: { line: { color: '#dc2626' } },
    showlegend: false,
  });

  // Moving averages
  ['sma_20', 'sma_50', 'sma_200', 'ema_12', 'ema_26'].forEach((key) => {
    if (visible.has(key) && s[key]?.length) {
      const meta = TOGGLE_GROUPS.find(t => t.key === key);
      traces.push({
        type: 'scatter', mode: 'lines', x: dates, y: s[key],
        name: meta.label,
        line: { color: meta.color, width: 1.5 },
      });
    }
  });

  if (visible.has('bollinger') && s.bb_upper?.length) {
    traces.push({
      type: 'scatter', mode: 'lines', x: dates, y: s.bb_upper,
      name: 'BB Upper', line: { color: '#a78bfa', width: 1, dash: 'dot' },
    });
    traces.push({
      type: 'scatter', mode: 'lines', x: dates, y: s.bb_lower,
      name: 'BB Lower', line: { color: '#a78bfa', width: 1, dash: 'dot' },
      fill: 'tonexty', fillcolor: 'rgba(167, 139, 250, 0.05)',
    });
  }

  // Fibonacci horizontals (drawn from latest indicators)
  if (visible.has('fibonacci') && ind.fibonacci) {
    const fib = ind.fibonacci;
    [0.236, 0.382, 0.5, 0.618, 0.786].forEach((ratio) => {
      const level = fib[`fib_${ratio.toFixed(3)}`];
      if (level == null) return;
      traces.push({
        type: 'scatter', mode: 'lines',
        x: [dates[0], dates[dates.length - 1]], y: [level, level],
        name: `Fib ${(ratio * 100).toFixed(1)}%`,
        line: { color: '#c9a96e', width: 1, dash: 'dash' },
        opacity: 0.6, showlegend: true,
      });
    });
  }

  // Pivot points
  if (visible.has('pivots') && ind.pivots?.P) {
    ['P', 'R1', 'R2', 'S1', 'S2'].forEach((p) => {
      const lvl = ind.pivots[p];
      if (lvl == null) return;
      traces.push({
        type: 'scatter', mode: 'lines',
        x: [dates[0], dates[dates.length - 1]], y: [lvl, lvl],
        name: p, line: { color: p === 'P' ? '#94a3b8' : p.startsWith('R') ? '#f87171' : '#86efac', width: 1, dash: 'dot' },
        opacity: 0.5,
      });
    });
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        {TOGGLE_GROUPS.map((t) => {
          const on = visible.has(t.key);
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => {
                const next = new Set(visible);
                on ? next.delete(t.key) : next.add(t.key);
                setVisible(next);
              }}
              className={`text-[10px] px-2 py-1 rounded border transition-colors ${
                on
                  ? 'bg-[#c9a96e]/10 border-[#c9a96e] text-[#c9a96e]'
                  : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      <PlotlyChart
        data={traces}
        layout={{
          height: 460,
          xaxis: { rangeslider: { visible: false } },
          yaxis: { tickformat: '.2f' },
        }}
      />
    </div>
  );
};

export default PriceChartWithOverlays;
